import { cn, useIsMobile } from '@heidi/utils';
import type { ReactNode } from 'react';

import { useIsRoutinesOwnedOpenLastRunEnabled } from '../../../flags';

export interface ProjectRunRecentRunsRailProps {
  /** The routine backing this chat, or `undefined` for a plain chat. */
  workflowId: string | undefined;
  /** Whether that routine has runs to list. */
  hasRuns: boolean;
  /** Whether the rail is open; it and the artifact canvas are mutually exclusive. */
  isOpen: boolean;
}

/** What the app supplies: the routine's recent-runs card, which the workflows root renders. */
export interface ProjectRunRecentRunsRailHost {
  renderRail: (workflowId: string) => ReactNode;
}

/**
 * The routine "Recent runs" card, mounted beside the whole project chat column.
 *
 * Renders nothing for a plain (non-routine) chat, and only from ``xl`` up —
 * narrower viewports have no room for the group, so between ``sm`` and ``xl``
 * the header toggle opens the same card in a popover instead (the routine-run
 * header), and on phones the configuration bottom sheet stays the path to run
 * history.
 *
 * The host treats this as a fixed-width sibling, so showing it pushes the
 * header, transcript, and composer together. Artifact canvas state remains
 * mutually exclusive through the rail's open state, which the host reads.
 *
 * Under {@link useIsRoutinesOwnedOpenLastRunEnabled}, the routine card (the
 * `{ type: 'workflow' }` artifact opened via the run-chat header toggle)
 * carries Recent runs itself, so this docked rail renders nothing.
 */
export const ProjectRunRecentRunsRail = ({
  workflowId,
  hasRuns,
  isOpen,
  renderRail,
}: ProjectRunRecentRunsRailProps & ProjectRunRecentRunsRailHost) => {
  const isMobile = useIsMobile();
  const isOwnedOpenLastRunEnabled = useIsRoutinesOwnedOpenLastRunEnabled();
  if (isMobile || workflowId === undefined || !hasRuns || isOwnedOpenLastRunEnabled) {
    return null;
  }

  return (
    <div
      data-testid="project-run-recent-runs-slot"
      className={cn(
        'hidden shrink-0 overflow-hidden motion-safe:transition-[width] motion-safe:duration-300 motion-safe:ease-out xl:block',
        isOpen ? 'xl:w-80' : 'xl:w-0'
      )}
    >
      {isOpen ? (
        <div className="flex items-start pe-4 ps-4 pt-3 motion-safe:duration-300 motion-safe:ease-out motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-right-3">
          {renderRail(workflowId)}
        </div>
      ) : null}
    </div>
  );
};
